if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // Optional mobile variant-media grouping.
        // Remains inactive unless enabled in the Theme Editor.
        this.groupVariantMedia = this.dataset.groupVariantMedia === 'true';
        this.variantMediaGrouping = null;
        this.variantMediaReorderInProgress = false;

        // HIDE MEDIA ON:
        // Keeps rapid thumbnail clicks locked to the latest requested media
        // until the main gallery physically reaches that media.
        this.requestedMediaId = null;

        // MAIN IMAGE -> THUMBNAIL:
        // Prevent duplicate slideChanged events for the same settled
        // main image from re-running thumbnail movement.
        this.lastSettledMainMediaId = null;

        // GROUP MEDIA OFF:
        // Track the variant's featured media separately from the image
        // the customer is currently viewing. This lets size-only variant
        // changes keep the current gallery image instead of jumping back
        // to the featured image.
        const initialActiveMedia =
          this.elements.viewer.querySelector(
            '[data-media-id].is-active'
          );

        this.lastVariantFeaturedMediaId =
          initialActiveMedia?.dataset.mediaId || null;

        // GROUP MEDIA OFF:
        // Newest manual thumbnail request wins.
        this.groupDisabledThumbnailRequestToken = 0;

        if (this.groupVariantMedia) {
          const groupingData = this.querySelector(
            '[data-variant-media-grouping]'
          );

          if (groupingData) {
            try {
              this.variantMediaGrouping = JSON.parse(
                groupingData.textContent
              );
            } catch (error) {
              console.error(
                'Unable to parse variant media grouping data.',
                error
              );
            }
          }
        }

        if (
          this.mql.matches &&
          this.dataset.showLeftThumbnailsDesktop === 'true' &&
          this.groupVariantMedia
        ) {
          const activeMedia = this.elements.viewer.querySelector(
            '[data-media-id].is-active'
          );

          if (activeMedia?.dataset.mediaId) {
            this.filterDesktopLeftThumbnails(
              activeMedia.dataset.mediaId
            );
          }
        }

        this.elements.viewer.addEventListener(
          'slideChanged',
          this.onSlideChanged.bind(this)
        );

        // MAIN IMAGE -> THUMBNAIL:
        // A real main-gallery interaction immediately gives control
        // back to the main image without any timer or delay.
        this.elements.viewer.addEventListener(
          'pointerdown',
          () => {
            const hideVariantMediaOnMobile =
              !this.mql.matches &&
              this.dataset.hideVariants === 'true' &&
              (
                this.dataset.hideVariantsScope === 'mobile' ||
                this.dataset.hideVariantsScope === 'both'
              );

            // A real main-gallery interaction always takes control,
            // regardless of Hide Media mode.
            this.requestedMediaId = null;
          },
          { passive: true }
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                () => {
                  if (
                    button.getAttribute('aria-current') ===
                    'true'
                  ) {
                    return;
                  }

                  if (!this.groupVariantMedia) {
                    this.groupDisabledThumbnailRequestToken += 1;

                    // GROUP MEDIA OFF — RAPID THUMBNAIL TAPS:
                    // Cancel only an older in-flight main-slider scroll before
                    // the newest thumbnail request starts. This prevents a
                    // previous smooth movement from carrying the gallery past
                    // the newly selected image after it has already been reached.
                    const mainSlider =
                      this.elements.viewer?.slider;

                    if (mainSlider) {
                      const previousScrollBehavior =
                        mainSlider.style.scrollBehavior;

                      mainSlider.style.scrollBehavior = 'auto';

                      const currentScrollLeft =
                        mainSlider.scrollLeft;

                      mainSlider.scrollTo({
                        left: currentScrollLeft,
                        behavior: 'auto',
                      });

                      mainSlider.style.scrollBehavior =
                        previousScrollBehavior;
                    }
                  }

                  this.setActiveMedia(
                    mediaToSwitch.dataset.target,
                    false,
                    !this.groupVariantMedia
                      ? this.groupDisabledThumbnailRequestToken
                      : null
                  );
                }
              );
            });

          if (
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }

          // DESKTOP ONLY:
          // Enable the vertical left-thumbnail carousel only when needed.
          if (
            this.mql.matches &&
            this.dataset.showLeftThumbnailsDesktop === 'true'
          ) {
            this.setupDesktopLeftThumbnailCarousel();
          }
        }

        // Apply variant-media grouping when the product page initially loads.
        // This handles products opened from a card swatch with ?variant=...
        // so the already-selected colour's media group starts at the front.
        if (this.groupVariantMedia && !this.mql.matches) {
          requestAnimationFrame(() => {
            const activeMedia = this.elements.viewer.querySelector(
              '[data-media-id].is-active'
            );

            if (!activeMedia?.dataset.mediaId) return;

            this.groupActiveVariantMedia(
              activeMedia.dataset.mediaId
            );
          });
        }
      }

 // Keep the main gallery and thumbnail selection in sync.
// Hidden-media and normal-media galleries are handled separately.
// The latest navigation action always becomes the source of truth.
onSlideChanged(event) {
  const viewerSlider =
    this.elements.viewer.slider;

  if (!viewerSlider) return;

  const hideVariantMediaOnMobile =
    !this.mql.matches &&
    this.dataset.hideVariants === 'true' &&
    (
      this.dataset.hideVariantsScope === 'mobile' ||
      this.dataset.hideVariantsScope === 'both'
    );

  // GROUPED MOBILE COLOUR CHANGE:
  // Ignore synthetic slideChanged events while the selected colour group
  // is being rebuilt into its final featured-first order.
  if (this.variantMediaReorderInProgress) {
    return;
  }

  const allMedia = [
    ...this.elements.viewer.querySelectorAll(
      '[data-media-id]'
    )
  ];

  let navigationMedia;

  if (hideVariantMediaOnMobile) {
    // HIDE VARIANT MEDIA ON:
    // Only visible media belongs to the physical slider sequence.
    navigationMedia = allMedia.filter((item) => {
      return (
        !item.hidden &&
        window.getComputedStyle(item).display !==
          'none'
      );
    });
  } else {
    // HIDE VARIANT MEDIA OFF:
    // Every media item belongs to the slider sequence.
    navigationMedia = allMedia;
  }

  if (!navigationMedia.length) return;

  // MAIN IMAGE -> THUMBNAIL:
  // For a real slider swipe, trust Dawn's currentElement first.
  // This keeps the selected thumbnail matched to the exact
  // main image that the slider says is active.
  const eventMedia =
    event.detail?.currentElement;

  const eventMediaIsUsable =
    eventMedia &&
    navigationMedia.includes(eventMedia) &&
    eventMedia.dataset.mediaId;

  let currentMedia;

  if (eventMediaIsUsable) {
    currentMedia = eventMedia;
  } else {
    // Fallback only when currentElement is unavailable/unusable.
    // Use the physically closest visible media.
    currentMedia =
      navigationMedia.reduce(
        (closest, item) => {
          const itemDistance = Math.abs(
            viewerSlider.scrollLeft -
              item.offsetLeft
          );

          const closestDistance = Math.abs(
            viewerSlider.scrollLeft -
              closest.offsetLeft
          );

          return itemDistance < closestDistance
            ? item
            : closest;
        },
        navigationMedia[0]
      );
  }

  const mediaId =
    currentMedia.dataset.mediaId;

  if (!mediaId) return;

  // MANUAL THUMBNAIL JUMP:
  // A programmatic jump can physically pass intermediate slides.
  // Ignore those slideChanged events in both Hide Media modes until
  // the exact thumbnail-requested media is reached.
  if (
    this.requestedMediaId &&
    mediaId !== this.requestedMediaId
  ) {
    return;
  }

  if (this.requestedMediaId === mediaId) {
    this.requestedMediaId = null;
  }

  // MAIN IMAGE -> THUMBNAIL:
  // Ignore duplicate slideChanged events for the same settled image.
  // A real A -> B -> A swipe still works because the media ID changes.
  if (
    hideVariantMediaOnMobile &&
    this.lastSettledMainMediaId === mediaId
  ) {
    return;
  }

  if (hideVariantMediaOnMobile) {
    this.lastSettledMainMediaId = mediaId;
  }

  if (this.elements.thumbnails) {
    const thumbnail =
      this.elements.thumbnails.querySelector(
        `[data-target="${mediaId}"]`
      );

    this.setActiveThumbnail(thumbnail);
  }
}

isMediaInSameVariantGroup(firstMediaId, secondMediaId) {
  if (
    !this.groupVariantMedia ||
    !this.variantMediaGrouping ||
    !firstMediaId ||
    !secondMediaId
  ) {
    return false;
  }

  const { mediaOrder, variantStarts } =
    this.variantMediaGrouping;

  if (
    !Array.isArray(mediaOrder) ||
    !Array.isArray(variantStarts) ||
    !mediaOrder.length ||
    !variantStarts.length
  ) {
    return false;
  }

  const firstIndex = mediaOrder.indexOf(firstMediaId);
  const secondIndex = mediaOrder.indexOf(secondMediaId);

  if (firstIndex === -1 || secondIndex === -1) {
    return false;
  }

  // Normal Printify/Shopify setup:
  // media is split into equal contiguous colour blocks.
  if (mediaOrder.length % variantStarts.length === 0) {
    const groupSize =
      mediaOrder.length / variantStarts.length;

    return (
      Math.floor(firstIndex / groupSize) ===
      Math.floor(secondIndex / groupSize)
    );
  }

  // Fallback: determine each media item's group from
  // the ordered variant-start anchors.
  const orderedStarts = variantStarts
    .map((mediaId) => mediaOrder.indexOf(mediaId))
    .filter((index) => index !== -1)
    .sort((a, b) => a - b);

  if (!orderedStarts.length) return false;

  const getGroupIndex = (mediaIndex) => {
    let groupIndex = -1;

    orderedStarts.forEach((startIndex, index) => {
      if (startIndex <= mediaIndex) {
        groupIndex = index;
      }
    });

    return groupIndex;
  };

  return (
    getGroupIndex(firstIndex) ===
    getGroupIndex(secondIndex)
  );
}

setActiveMedia(mediaId, prepend, thumbnailRequestToken = null) {
  const activeMedia =
    this.elements.viewer.querySelector(
      `[data-media-id="${mediaId}"]`
    ) ||
    this.elements.viewer.querySelector(
      '[data-media-id]'
    );

  if (!activeMedia) return;

  if (
    !this.groupVariantMedia &&
    !prepend &&
    thumbnailRequestToken !== null &&
    thumbnailRequestToken !==
      this.groupDisabledThumbnailRequestToken
  ) {
    return;
  }

  // GROUP MEDIA OFF — SIZE-ONLY VARIANT CHANGE:
  // If Shopify points the new variant to the same featured media as
  // the previous variant, only size changed. Keep the image the
  // customer is currently viewing.
  if (prepend && !this.groupVariantMedia) {
    const requestedFeaturedMediaId =
      activeMedia.dataset.mediaId;

    if (
      this.lastVariantFeaturedMediaId &&
      requestedFeaturedMediaId ===
        this.lastVariantFeaturedMediaId
    ) {
      return;
    }

    this.lastVariantFeaturedMediaId =
      requestedFeaturedMediaId;
  }

  // SIZE-ONLY VARIANT CHANGE:
  // If the requested media is in the same colour group
  // as the currently selected image, do not move the gallery.
  if (prepend && this.groupVariantMedia) {
    const currentMedia =
      this.elements.viewer.querySelector(
        '[data-media-id].is-active'
      );

    const currentMediaId =
      currentMedia?.dataset.mediaId;

    const requestedMediaId =
      activeMedia.dataset.mediaId;

    if (
      currentMediaId &&
      requestedMediaId &&
      this.isMediaInSameVariantGroup(
        currentMediaId,
        requestedMediaId
      )
    ) {
      return;
    }
  }

  if (prepend && this.groupVariantMedia) {
    this.lastVariantFeaturedMediaId =
      activeMedia.dataset.mediaId;
  }

  const hideVariantMediaOnMobile =
    !this.mql.matches &&
    this.dataset.hideVariants === 'true' &&
    (
      this.dataset.hideVariantsScope === 'mobile' ||
      this.dataset.hideVariantsScope === 'both'
    );

  // MANUAL THUMBNAIL NAVIGATION:
  // In Hide Media ON, keep the existing lock behavior.
  // In Hide Media OFF, also lock programmatic thumbnail jumps so
  // intermediate slides crossed by scrollTo() cannot become active.
  if (!prepend) {
    this.requestedMediaId =
      activeMedia.dataset.mediaId;
  } else if (hideVariantMediaOnMobile) {
    this.requestedMediaId = null;
  }

  // Every new thumbnail/variant selection invalidates
  // delayed work belonging to an older interaction.
  this.navigationToken =
    (this.navigationToken ?? 0) + 1;

  const navigationToken =
    this.navigationToken;

  // Cancel an older pending main-gallery scroll.
  window.clearTimeout(
    this.activeMediaScrollTimer
  );

  const mobileVariantChange =
    prepend &&
    !this.mql.matches;

  const groupedMobileVariantChange =
    mobileVariantChange &&
    this.groupVariantMedia;

  if (!mobileVariantChange) {
    this.elements.viewer
      .querySelectorAll('[data-media-id]')
      .forEach((element) => {
        element.classList.remove('is-active');
      });

    activeMedia.classList.add('is-active');
  }

  if (prepend) {
    if (
      this.mql.matches &&
      this.dataset.showLeftThumbnailsDesktop === 'true' &&
      this.groupVariantMedia
    ) {
      // DESKTOP ONLY:
      // Filter only the left thumbnail rail for the selected variant.
      this.filterDesktopLeftThumbnails(mediaId);
    }

    if (
      this.groupVariantMedia &&
      !this.mql.matches
    ) {
      // Both grouped modes use the same atomic reorder guard.
      // Hide Media itself still controls which media participates in navigation.
      this.variantMediaReorderInProgress = true;
      this.groupActiveVariantMedia(mediaId);
      this.variantMediaReorderInProgress = false;

      this.elements.viewer
        .querySelectorAll('[data-media-id]')
        .forEach((element) => {
          element.classList.remove('is-active');
        });

      activeMedia.classList.add('is-active');
    } else {
      if (mobileVariantChange) {
        const viewerSlider =
          this.elements.viewer?.slider;
        const thumbnailSlider =
          this.elements.thumbnails?.slider;

        // GROUP MEDIA OFF — MOBILE COLOUR CHANGE:
        // Return both carousels to their starting point before moving
        // the newly selected variant's featured media to the front.
        if (viewerSlider) {
          const previousScrollBehavior =
            viewerSlider.style.scrollBehavior;
          const previousScrollSnapType =
            viewerSlider.style.scrollSnapType;

          viewerSlider.style.scrollBehavior = 'auto';
          viewerSlider.style.scrollSnapType = 'none';
          viewerSlider.scrollLeft = 0;
          viewerSlider.style.scrollBehavior =
            previousScrollBehavior;
          viewerSlider.style.scrollSnapType =
            previousScrollSnapType;
        }

        if (thumbnailSlider) {
          const previousScrollBehavior =
            thumbnailSlider.style.scrollBehavior;
          const previousScrollSnapType =
            thumbnailSlider.style.scrollSnapType;

          thumbnailSlider.style.scrollBehavior = 'auto';
          thumbnailSlider.style.scrollSnapType = 'none';
          thumbnailSlider.scrollLeft = 0;
          thumbnailSlider.style.scrollBehavior =
            previousScrollBehavior;
          thumbnailSlider.style.scrollSnapType =
            previousScrollSnapType;
        }
      }

      if (
        activeMedia.parentElement.firstChild !==
        activeMedia
      ) {
        activeMedia.parentElement.prepend(
          activeMedia
        );
      }

      if (this.elements.thumbnails) {
        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        if (
          activeThumbnail &&
          activeThumbnail.parentElement.firstChild !==
            activeThumbnail
        ) {
          activeThumbnail.parentElement.prepend(
            activeThumbnail
          );
        }
      }

      if (this.elements.viewer.slider) {
        this.elements.viewer.resetPages();

        if (mobileVariantChange) {
          const viewerSlider =
            this.elements.viewer.slider;
          const previousScrollBehavior =
            viewerSlider.style.scrollBehavior;
          const previousScrollSnapType =
            viewerSlider.style.scrollSnapType;

          viewerSlider.style.scrollBehavior = 'auto';
          viewerSlider.style.scrollSnapType = 'none';
          viewerSlider.scrollLeft = 0;
          viewerSlider.style.scrollBehavior =
            previousScrollBehavior;
          viewerSlider.style.scrollSnapType =
            previousScrollSnapType;
        }
      }

      if (
        mobileVariantChange &&
        this.elements.thumbnails?.slider
      ) {
        this.elements.thumbnails.resetPages();

        const thumbnailSlider =
          this.elements.thumbnails.slider;
        const previousScrollBehavior =
          thumbnailSlider.style.scrollBehavior;
        const previousScrollSnapType =
          thumbnailSlider.style.scrollSnapType;

        thumbnailSlider.style.scrollBehavior = 'auto';
        thumbnailSlider.style.scrollSnapType = 'none';
        thumbnailSlider.scrollLeft = 0;
        thumbnailSlider.style.scrollBehavior =
          previousScrollBehavior;
        thumbnailSlider.style.scrollSnapType =
          previousScrollSnapType;
      }

      if (mobileVariantChange) {
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');
      }
    }
  }

  // FINAL MOBILE COLOUR-CHANGE REVEAL:
  // Runs once after either Group Media mode has completed its variant change.
  if (mobileVariantChange) {
    activeMedia.classList.remove(
      'variant-change-reveal'
    );

    void activeMedia.offsetWidth;

    activeMedia.classList.add(
      'variant-change-reveal'
    );

    activeMedia.addEventListener(
      'animationend',
      () => {
        activeMedia.classList.remove(
          'variant-change-reveal'
        );
      },
      { once: true }
    );
  }

  this.preventStickyHeader();

  // Mobile colour changes are fully positioned synchronously above.
  // Do not run a second delayed gallery scroll for either Group Media mode.
  if (!mobileVariantChange) {
    this.activeMediaScrollTimer =
      window.setTimeout(() => {
        if (
          navigationToken !==
          this.navigationToken
        ) {
          return;
        }

        if (
          !this.groupVariantMedia &&
          !prepend &&
          thumbnailRequestToken !== null &&
          thumbnailRequestToken !==
            this.groupDisabledThumbnailRequestToken
        ) {
          return;
        }

        if (
          !this.mql.matches ||
          this.elements.thumbnails
        ) {
          const mediaSlider =
            activeMedia.parentElement;

          if (
            !this.groupVariantMedia &&
            !prepend &&
            thumbnailRequestToken !== null
          ) {
            // GROUP MEDIA OFF — MANUAL THUMBNAIL JUMP:
            // Make this one main-image move atomic. A smooth/snapped move can
            // reach the requested slide, clear the request lock, then finish
            // one snap farther and overwrite the selection.
            const previousScrollBehavior =
              mediaSlider.style.scrollBehavior;
            const previousScrollSnapType =
              mediaSlider.style.scrollSnapType;

            mediaSlider.style.scrollBehavior = 'auto';
            mediaSlider.style.scrollSnapType = 'none';

            mediaSlider.scrollLeft =
              activeMedia.offsetLeft;

            mediaSlider.style.scrollBehavior =
              previousScrollBehavior;
            mediaSlider.style.scrollSnapType =
              previousScrollSnapType;
          } else {
            mediaSlider.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
        }

        const activeMediaRect =
          activeMedia.getBoundingClientRect();

        if (activeMediaRect.top > -0.5) {
          return;
        }

        const top =
          activeMediaRect.top + window.scrollY;

        window.scrollTo({
          top,
          behavior: 'smooth',
        });
      }, 0);
  }

  this.playActiveMedia(activeMedia);

  if (!this.elements.thumbnails) return;

  const activeThumbnail =
    this.elements.thumbnails.querySelector(
      `[data-target="${mediaId}"]`
    );

  if (
    !this.groupVariantMedia &&
    !prepend &&
    thumbnailRequestToken !== null &&
    thumbnailRequestToken !==
      this.groupDisabledThumbnailRequestToken
  ) {
    return;
  }

  if (mobileVariantChange) {
    if (activeThumbnail) {
      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach((button) => {
          button.removeAttribute('aria-current');
        });

      activeThumbnail
        .querySelector('button')
        ?.setAttribute('aria-current', 'true');

      this.lastActiveThumbnailTarget =
        activeThumbnail.dataset.target;
    }
  } else {
    this.setActiveThumbnail(activeThumbnail);
  }

  if (activeThumbnail) {
    this.announceLiveRegion(
      activeMedia,
      activeThumbnail.dataset.mediaPosition
    );
  }
}

      // DESKTOP ONLY:
      // Filter the left thumbnail rail to the selected variant group.
      // Main desktop media remains untouched.
      filterDesktopLeftThumbnails(mediaId) {
        if (
          !this.mql.matches ||
          this.dataset.showLeftThumbnailsDesktop !== 'true' ||
          !this.groupVariantMedia ||
          !this.variantMediaGrouping ||
          !this.elements.thumbnails
        ) {
          return;
        }

        const { mediaOrder, variantStarts } =
          this.variantMediaGrouping;

        if (
          !Array.isArray(mediaOrder) ||
          !Array.isArray(variantStarts) ||
          !mediaOrder.length ||
          !variantStarts.length
        ) {
          return;
        }

        const selectedMediaIndex =
          mediaOrder.indexOf(mediaId);

        if (selectedMediaIndex === -1) return;

        let activeGroup = [];

        // DESKTOP ONLY:
        // Prefer equal contiguous colour blocks when the product media
        // divides cleanly by the number of variant featured-media anchors.
        // The image count per colour is calculated dynamically.
        const hasEqualColourBlocks =
          mediaOrder.length %
            variantStarts.length ===
          0;

        if (hasEqualColourBlocks) {
          const groupSize =
            mediaOrder.length /
            variantStarts.length;

          const groupIndex = Math.floor(
            selectedMediaIndex / groupSize
          );

          const groupStartIndex =
            groupIndex * groupSize;

          const groupEndIndex =
            groupStartIndex + groupSize;

          activeGroup = mediaOrder.slice(
            groupStartIndex,
            groupEndIndex
          );
        } else {
          // Fallback for unusual products where media cannot be divided
          // evenly into colour blocks. Treat variantStarts as anchors.
          const orderedVariantStarts =
            variantStarts
              .map((startMediaId) => ({
                mediaId: startMediaId,
                index:
                  mediaOrder.indexOf(startMediaId),
              }))
              .filter((item) => item.index !== -1)
              .sort((a, b) => a.index - b.index);

          if (!orderedVariantStarts.length) return;

          let activeStartPosition = -1;

          orderedVariantStarts.forEach(
            (item, index) => {
              if (
                item.index <=
                selectedMediaIndex
              ) {
                activeStartPosition = index;
              }
            }
          );

          if (activeStartPosition === -1) {
            return;
          }

          const groupStartIndex =
            orderedVariantStarts[
              activeStartPosition
            ].index;

          const nextGroup =
            orderedVariantStarts[
              activeStartPosition + 1
            ];

          const groupEndIndex =
            nextGroup
              ? nextGroup.index
              : mediaOrder.length;

          if (
            groupEndIndex <=
            groupStartIndex
          ) {
            return;
          }

          activeGroup = mediaOrder.slice(
            groupStartIndex,
            groupEndIndex
          );
        }

        if (!activeGroup.length) return;

        // GROUPING ON:
        // Keep only the selected variant group's media visible
        // in the main gallery. Other variant groups must not remain
        // part of the visible carousel sequence.
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((mediaItem) => {
            mediaItem.hidden =
              !activeGroup.includes(
                mediaItem.dataset.mediaId
              );
          });

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((thumbnail) => {
            thumbnail.hidden =
              !activeGroup.includes(
                thumbnail.dataset.target
              );
          });

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.resetPages();
        }

        this.updateDesktopLeftThumbnailCarousel();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.updateDesktopLeftThumbnailCarousel();
          });
        });
      }

      // DESKTOP ONLY:
      // Shared vertical left-thumbnail rail for grouped and non-grouped galleries.
      setupDesktopLeftThumbnailCarousel() {
        if (
          !this.mql.matches ||
          this.dataset.showLeftThumbnailsDesktop !== 'true' ||
          !this.elements.thumbnails?.slider
        ) {
          return;
        }

        const component =
          this.elements.thumbnails;

        const slider =
          component.slider;

        const previousButton =
          component.querySelector(
            '.slider-button--prev'
          );

        const nextButton =
          component.querySelector(
            '.slider-button--next'
          );

        if (!previousButton || !nextButton) return;

        this.desktopLeftThumbnailPreviousButton =
          previousButton;

        this.desktopLeftThumbnailNextButton =
          nextButton;

        previousButton.addEventListener(
          'click',
          (event) => {
            if (
              !this.mql.matches ||
              this.dataset.showLeftThumbnailsDesktop !== 'true'
            ) {
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            this.scrollDesktopLeftThumbnails(-1);
          },
          true
        );

        nextButton.addEventListener(
          'click',
          (event) => {
            if (
              !this.mql.matches ||
              this.dataset.showLeftThumbnailsDesktop !== 'true'
            ) {
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            this.scrollDesktopLeftThumbnails(1);
          },
          true
        );

        slider.addEventListener(
          'scroll',
          () => {
            this.updateDesktopLeftThumbnailButtons();
          },
          { passive: true }
        );

        window.addEventListener(
          'resize',
          () => {
            if (
              this.mql.matches &&
              this.dataset.showLeftThumbnailsDesktop === 'true'
            ) {
              this.updateDesktopLeftThumbnailCarousel();
            }
          },
          { passive: true }
        );

        this.updateDesktopLeftThumbnailCarousel();

        // DESKTOP ONLY:
        // Re-measure after the initial desktop layout has actually settled.
        // No timer is used; animation frames wait for browser layout/paint.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.updateDesktopLeftThumbnailCarousel();
          });
        });

        // Main media dimensions can change once its image finishes loading.
        // Re-check overflow at that point so the rail uses the final viewer height.
        this.elements.viewer
          .querySelectorAll('img')
          .forEach((image) => {
            if (image.complete) return;

            image.addEventListener(
              'load',
              () => {
                if (
                  this.mql.matches &&
                  this.dataset.showLeftThumbnailsDesktop === 'true'
                ) {
                  this.updateDesktopLeftThumbnailCarousel();
                }
              },
              { once: true }
            );
          });
      }

      // DESKTOP ONLY:
      // Static rail when the complete natural stack fits inside the main image.
      // If top button + thumbnails + gaps + bottom button would exceed the
      // main-image height, switch to the constrained vertical carousel.
      updateDesktopLeftThumbnailCarousel() {
        if (
          !this.mql.matches ||
          this.dataset.showLeftThumbnailsDesktop !== 'true' ||
          !this.elements.thumbnails?.slider
        ) {
          return;
        }

        const component =
          this.elements.thumbnails;

        const slider =
          component.slider;

        const viewerHeight =
          this.elements.viewer
            ?.getBoundingClientRect()
            .height || 0;

        if (viewerHeight <= 0) return;

        component.style.height =
          `${viewerHeight}px`;

        component.style.maxHeight =
          `${viewerHeight}px`;

        const visibleThumbnails = [
          ...slider.querySelectorAll('[data-target]')
        ].filter((thumbnail) => {
          return (
            !thumbnail.hidden &&
            window.getComputedStyle(thumbnail).display !== 'none'
          );
        });

        const sliderStyles =
          window.getComputedStyle(slider);

        const thumbnailGap =
          parseFloat(sliderStyles.rowGap) ||
          parseFloat(sliderStyles.gap) ||
          0;

        const thumbnailsHeight =
          visibleThumbnails.reduce(
            (total, thumbnail) =>
              total +
              thumbnail.getBoundingClientRect().height,
            0
          ) +
          Math.max(0, visibleThumbnails.length - 1) *
            thumbnailGap;

        const previousButtonHeight =
          this.desktopLeftThumbnailPreviousButton
            ?.getBoundingClientRect()
            .height || 0;

        const nextButtonHeight =
          this.desktopLeftThumbnailNextButton
            ?.getBoundingClientRect()
            .height || 0;

        // Matches the static CSS spacing:
        // 0.8rem below the top button and 0.8rem above the bottom button.
        const rootFontSize =
          parseFloat(
            window.getComputedStyle(
              document.documentElement
            ).fontSize
          ) || 10;

        const staticControlGap =
          rootFontSize * 0.8;

        const naturalRailHeight =
          previousButtonHeight +
          staticControlGap +
          thumbnailsHeight +
          staticControlGap +
          nextButtonHeight;

        const hasOverflow =
          naturalRailHeight > viewerHeight + 1;

        component.classList.toggle(
          'desktop-left-thumbnail-carousel--active',
          hasOverflow
        );

        slider.style.overflowX = 'hidden';
        slider.style.overflowY =
          hasOverflow ? 'auto' : 'visible';
        slider.style.scrollBehavior = 'smooth';

        // Both controls remain visible in the static design.
        // CSS makes them non-functional until carousel mode is active.
        if (
          this.desktopLeftThumbnailPreviousButton
        ) {
          this.desktopLeftThumbnailPreviousButton.hidden =
            false;
        }

        if (
          this.desktopLeftThumbnailNextButton
        ) {
          this.desktopLeftThumbnailNextButton.hidden =
            false;
        }

        if (!hasOverflow) {
          slider.scrollTop = 0;
        }

        this.updateDesktopLeftThumbnailButtons();
      }

      // DESKTOP ONLY:
      // Move about three visible thumbnails per arrow press.
      scrollDesktopLeftThumbnails(direction) {
        const slider =
          this.elements.thumbnails?.slider;

        if (!slider) return;

        const visibleThumbnails = [
          ...slider.querySelectorAll(
            '[data-target]'
          )
        ].filter((thumbnail) => {
          return (
            !thumbnail.hidden &&
            window.getComputedStyle(thumbnail).display !==
              'none'
          );
        });

        const firstThumbnail =
          visibleThumbnails[0];

        if (!firstThumbnail) return;

        const thumbnailHeight =
          firstThumbnail.getBoundingClientRect().height;

        const sliderStyles =
          window.getComputedStyle(slider);

        const gap =
          parseFloat(sliderStyles.rowGap) ||
          parseFloat(sliderStyles.gap) ||
          0;

        slider.scrollBy({
          top:
            direction *
            (thumbnailHeight + gap) *
            3,
          behavior: 'smooth',
        });
      }

      // DESKTOP ONLY:
      // Remove the normal thumbnail outline only while that thumbnail
      // physically overlaps the top or bottom arrow control.
      updateDesktopThumbnailControlOverlap() {
        if (
          !this.mql.matches ||
          this.dataset.showLeftThumbnailsDesktop !== 'true' ||
          !this.elements.thumbnails?.slider
        ) {
          return;
        }

        const slider =
          this.elements.thumbnails.slider;

        const previousButton =
          this.desktopLeftThumbnailPreviousButton;

        const nextButton =
          this.desktopLeftThumbnailNextButton;

        const previousRect =
          previousButton &&
          !previousButton.hidden
            ? previousButton.getBoundingClientRect()
            : null;

        const nextRect =
          nextButton &&
          !nextButton.hidden
            ? nextButton.getBoundingClientRect()
            : null;

        slider
          .querySelectorAll('[data-target]')
          .forEach((thumbnail) => {
            const isVisible =
              !thumbnail.hidden &&
              window.getComputedStyle(thumbnail).display !==
                'none';

            if (!isVisible) {
              thumbnail.classList.remove(
                'desktop-thumbnail-under-control'
              );
              return;
            }

            const thumbnailRect =
              thumbnail.getBoundingClientRect();

            const overlapsPrevious =
              previousRect &&
              thumbnailRect.bottom > previousRect.top &&
              thumbnailRect.top < previousRect.bottom;

            const overlapsNext =
              nextRect &&
              thumbnailRect.bottom > nextRect.top &&
              thumbnailRect.top < nextRect.bottom;

            thumbnail.classList.toggle(
              'desktop-thumbnail-under-control',
              Boolean(
                overlapsPrevious ||
                overlapsNext
              )
            );
          });
      }

      // DESKTOP ONLY:
      // Keep carousel arrows natively enabled at all times.
      // At either scroll edge, scrollBy() simply has nowhere further to move.
      updateDesktopLeftThumbnailButtons() {
        if (
          !this.mql.matches ||
          this.dataset.showLeftThumbnailsDesktop !== 'true' ||
          !this.elements.thumbnails?.slider
        ) {
          return;
        }

        if (
          this.desktopLeftThumbnailPreviousButton
        ) {
          this.desktopLeftThumbnailPreviousButton.disabled =
            false;
        }

        if (
          this.desktopLeftThumbnailNextButton
        ) {
          this.desktopLeftThumbnailNextButton.disabled =
            false;
        }

        this.updateDesktopThumbnailControlOverlap();
      }

      // Groups the selected variant's media together on mobile.
      // Runs only when the Theme Editor option is enabled.
      //
      // Shopify's featured media is treated as an anchor INSIDE
      // its colour block, not as the first image of that block.
      groupActiveVariantMedia(mediaId) {
        if (
          !this.groupVariantMedia ||
          this.mql.matches ||
          !this.variantMediaGrouping
        ) {
          return;
        }

        const {
          mediaOrder,
          variantStarts,
        } = this.variantMediaGrouping;

        if (
          !Array.isArray(mediaOrder) ||
          !Array.isArray(variantStarts) ||
          mediaOrder.length === 0 ||
          variantStarts.length === 0
        ) {
          return;
        }

        const selectedMediaIndex =
          mediaOrder.indexOf(mediaId);

        if (selectedMediaIndex === -1) return;

        let activeGroup = [];

        // Preferred Printify/Shopify mode:
        //
        // Shopify gallery is sorted into contiguous colour blocks.
        // Each colour has the same number of selected Printify
        // mockups.
        //
        // IMPORTANT:
        // Nothing here is hardcoded to 3, 4, 5, etc.
        // The number of images per colour is calculated dynamically.
        const hasEqualColourBlocks =
          mediaOrder.length %
            variantStarts.length ===
          0;

        if (hasEqualColourBlocks) {
          const groupSize =
            mediaOrder.length /
            variantStarts.length;

          const groupIndex = Math.floor(
            selectedMediaIndex / groupSize
          );

          const groupStartIndex =
            groupIndex * groupSize;

          const groupEndIndex =
            groupStartIndex + groupSize;

          activeGroup = mediaOrder.slice(
            groupStartIndex,
            groupEndIndex
          );
        } else {
          // Legacy fallback:
          //
          // If the media cannot be divided into equal colour
          // blocks, use the original starter-anchor system.
          //
          // This prevents us from guessing incorrect group
          // boundaries on unusual products.
          const activeStartIndex =
            variantStarts.indexOf(mediaId);

          if (activeStartIndex === -1) return;

          const groupStartIndex =
            mediaOrder.indexOf(mediaId);

          if (groupStartIndex === -1) return;

          const nextVariantStart =
            variantStarts[
              activeStartIndex + 1
            ];

          const groupEndIndex =
            nextVariantStart
              ? mediaOrder.indexOf(
                  nextVariantStart
                )
              : mediaOrder.length;

          if (
            groupEndIndex === -1 ||
            groupEndIndex <= groupStartIndex
          ) {
            return;
          }

          activeGroup = mediaOrder.slice(
            groupStartIndex,
            groupEndIndex
          );
        }

        if (!activeGroup.length) return;

        const viewerSlider =
          this.elements.viewer?.slider;
        const thumbnailSlider =
          this.elements.thumbnails?.slider;

        if (viewerSlider) {
          const previousScrollBehavior =
            viewerSlider.style.scrollBehavior;
          const previousScrollSnapType =
            viewerSlider.style.scrollSnapType;

          viewerSlider.style.scrollBehavior = 'auto';
          viewerSlider.style.scrollSnapType = 'none';
          viewerSlider.scrollLeft = 0;
          viewerSlider.style.scrollBehavior =
            previousScrollBehavior;
          viewerSlider.style.scrollSnapType =
            previousScrollSnapType;
        }

        if (thumbnailSlider) {
          const previousScrollBehavior =
            thumbnailSlider.style.scrollBehavior;
          const previousScrollSnapType =
            thumbnailSlider.style.scrollSnapType;

          thumbnailSlider.style.scrollBehavior = 'auto';
          thumbnailSlider.style.scrollSnapType = 'none';
          thumbnailSlider.scrollLeft = 0;
          thumbnailSlider.style.scrollBehavior =
            previousScrollBehavior;
          thumbnailSlider.style.scrollSnapType =
            previousScrollSnapType;
        }

        // The selected variant's featured media is intentionally always first.
        // This applies in both grouped modes. In Hide Media ON mode the featured
        // media may have been hidden before the colour was selected, so current
        // visibility must never decide whether it becomes first.
        const featuredIndex = activeGroup.indexOf(mediaId);

        if (featuredIndex > 0) {
          activeGroup = [
            mediaId,
            ...activeGroup.filter(
              (groupMediaId) => groupMediaId !== mediaId
            ),
          ];
        }

        // GROUP MEDIA ON:
        // Only the currently selected variant/colour group belongs to the
        // visible carousel. All other variant media and thumbnails are hidden.
        // This is independent of the separate Hide Media mode; that mode still
        // controls its own featured-media behaviour, while grouping controls
        // which colour group's media is navigable.
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((item) => {
            const belongsToActiveGroup =
              activeGroup.includes(
                item.dataset.mediaId
              );

            item.hidden = !belongsToActiveGroup;

            // Dawn/theme CSS can override the HTML hidden attribute.
            // Explicitly remove non-selected variant media from layout so
            // they cannot remain physical slides in the mobile carousel.
            item.style.display =
              belongsToActiveGroup ? '' : 'none';
          });

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((thumbnail) => {
              const belongsToActiveGroup =
                activeGroup.includes(
                  thumbnail.dataset.target
                );

              thumbnail.hidden =
                !belongsToActiveGroup;

              thumbnail.style.display =
                belongsToActiveGroup ? '' : 'none';
            });
        }

        const viewerList =
          this.elements.viewer?.querySelector(
            'ul'
          );

        if (viewerList) {
          activeGroup
            .map((groupMediaId) =>
              viewerList.querySelector(
                `[data-media-id="${groupMediaId}"]`
              )
            )
            .filter(Boolean)
            .reverse()
            .forEach((item) => {
              viewerList.prepend(item);
            });
        }

        const thumbnailList =
          this.elements.thumbnails?.querySelector(
            'ul'
          );

        if (thumbnailList) {
          activeGroup
            .map((groupMediaId) =>
              thumbnailList.querySelector(
                `[data-target="${groupMediaId}"]`
              )
            )
            .filter(Boolean)
            .reverse()
            .forEach((item) => {
              thumbnailList.prepend(item);
            });
        }

        // The selected featured media is now physically first.
        // Rebuild pages and keep both sliders at position 0. The active
        // media/thumbnail state is applied only after this reorder completes.
        if (viewerSlider) {
          const previousScrollBehavior =
            viewerSlider.style.scrollBehavior;
          const previousScrollSnapType =
            viewerSlider.style.scrollSnapType;

          viewerSlider.style.scrollBehavior = 'auto';
          viewerSlider.style.scrollSnapType = 'none';

          this.elements.viewer.resetPages();
          viewerSlider.scrollLeft = 0;

          viewerSlider.style.scrollBehavior =
            previousScrollBehavior;
          viewerSlider.style.scrollSnapType =
            previousScrollSnapType;
        }

        if (thumbnailSlider) {
          const previousScrollBehavior =
            thumbnailSlider.style.scrollBehavior;
          const previousScrollSnapType =
            thumbnailSlider.style.scrollSnapType;

          thumbnailSlider.style.scrollBehavior = 'auto';
          thumbnailSlider.style.scrollSnapType = 'none';

          this.elements.thumbnails.resetPages();
          thumbnailSlider.scrollLeft = 0;

          thumbnailSlider.style.scrollBehavior =
            previousScrollBehavior;
          thumbnailSlider.style.scrollSnapType =
            previousScrollSnapType;
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute(
              'aria-current'
            );
          });

        const button =
          thumbnail.querySelector('button');

        if (!button) return;

        button.setAttribute(
          'aria-current',
          true
        );

        const slider =
          this.elements.thumbnails.slider;

        if (!slider) return;

        // DESKTOP ONLY:
        // Keep the active thumbnail visible inside the vertical rail.
        if (
          this.mql.matches &&
          this.dataset.showLeftThumbnailsDesktop === 'true'
        ) {
          const sliderRect =
            slider.getBoundingClientRect();

          const thumbnailRect =
            thumbnail.getBoundingClientRect();

          if (
            thumbnailRect.top < sliderRect.top ||
            thumbnailRect.bottom > sliderRect.bottom
          ) {
            thumbnail.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth',
            });
          }

          this.updateDesktopLeftThumbnailButtons();
          return;
        }

        const hideVariantMediaOnMobile =
          !this.mql.matches &&
          this.dataset.hideVariants === 'true' &&
          (
            this.dataset.hideVariantsScope === 'mobile' ||
            this.dataset.hideVariantsScope === 'both'
          );

        const allThumbnails = [
          ...this.elements.thumbnails.querySelectorAll(
            '[data-target]'
          )
        ];

        let navigationThumbnails;

        if (hideVariantMediaOnMobile) {
          // HIDE MEDIA ON:
          // Only visible thumbnails belong to navigation.
          navigationThumbnails =
            allThumbnails.filter((item) => {
              return (
                !item.hidden &&
                window.getComputedStyle(item).display !==
                  'none'
              );
            });
        } else {
          // HIDE MEDIA OFF:
          // Every thumbnail belongs to navigation.
          navigationThumbnails =
            allThumbnails;
        }

        const currentIndex =
          navigationThumbnails.indexOf(thumbnail);

        const previousThumbnail =
          this.lastActiveThumbnailTarget
            ? this.elements.thumbnails.querySelector(
                `[data-target="${this.lastActiveThumbnailTarget}"]`
              )
            : null;

        const previousIndex =
          previousThumbnail
            ? navigationThumbnails.indexOf(
                previousThumbnail
              )
            : -1;

        const isMovingLeft =
          currentIndex !== -1 &&
          previousIndex !== -1 &&
          currentIndex < previousIndex;

        this.lastActiveThumbnailTarget =
          thumbnail.dataset.target;

        // Moving left:
        // reveal two earlier thumbnails from the
        // correct navigation sequence.
        if (
          isMovingLeft &&
          slider.scrollLeft > 0
        ) {
          const sliderRect =
            slider.getBoundingClientRect();

          const thumbnailRect =
            thumbnail.getBoundingClientRect();

          const leftEdgeThreshold = 10;

          if (
            thumbnailRect.left - sliderRect.left <
            leftEdgeThreshold
          ) {
            const twoBackIndex =
              Math.max(0, currentIndex - 2);

            const revealTarget =
              navigationThumbnails[twoBackIndex] ||
              thumbnail;

            slider.scrollTo({
              left: revealTarget.offsetLeft,
              behavior: 'smooth',
            });

            return;
          }
        }

        // Right movement and normal positioning
        // continue using Dawn's normal behaviour.
        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}